import { useEffect, useRef } from "react";
import { m } from "framer-motion";
import { Star } from "lucide-react";
import { siteConfig } from "@/lib/site-config";
import { useFirstInteraction } from "@/hooks/useFirstInteraction";

/**
 * Reputation Hub review widget — preserves original embed height,
 * no clipping, no scrollbars, no forced max-height.
 *
 * The iframe loads its own gstatic fonts and is a known CLS source, and it sits
 * well below the fold, so both the script and the iframe are deferred until the
 * visitor's first interaction ([[useFirstInteraction]]). The wrapper reserves
 * height so the swap never shifts the page.
 */
const ReviewWidget = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const interacted = useFirstInteraction();

  useEffect(() => {
    if (!interacted) return;
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, [interacted]);

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </m.h2>
          <m.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-1 mb-3"
          >
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-secondary text-secondary" />
            ))}
          </m.div>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Real reviews from real Indiana & Michigan customers.
          </p>
        </div>

        <m.div
          ref={wrapRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="review-widget-wrap max-w-5xl mx-auto"
          style={{ minHeight: 420 }}
        >
          {interacted ? (
            <iframe
              className="lc_reviews_widget"
              src={siteConfig.widgets.reviewIframeSrc}
              frameBorder={0}
              scrolling="no"
              style={{ minWidth: "100%", width: "100%", border: "none", display: "block", minHeight: 420 }}
              title="Customer reviews"
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" style={{ minHeight: 420 }} aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <div className="mb-3 flex gap-1">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} className="h-4 w-4 fill-secondary text-secondary" />
                    ))}
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-5/6 rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </m.div>
      </div>
    </section>
  );
};

export default ReviewWidget;
